export type TigerwebBoundaryLevel = 'state' | 'county' | 'city'

export type TigerwebFeatureCollection = GeoJSON.FeatureCollection

export type ResolvedJurisdiction = {
  level: TigerwebBoundaryLevel
  geoid: string
  name: string
  basename: string
  /** Two-letter state abbrev when available (state layer STUSAB). */
  stateAbbr: string | null
  stateFips: string | null
}

export type ResolvedJurisdictionsAtPoint = {
  state: ResolvedJurisdiction | null
  county: ResolvedJurisdiction | null
  city: ResolvedJurisdiction | null
}

const TIGERWEB_STATE_COUNTY =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer'
const TIGERWEB_PLACES =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Places_CouSub_ConCity_SubMCD/MapServer'

/** Stable query layers (ACS 2025 / Current Places). */
const LAYER_BY_LEVEL: Record<TigerwebBoundaryLevel, { base: string; layerId: number }> = {
  state: { base: TIGERWEB_STATE_COUNTY, layerId: 26 },
  county: { base: TIGERWEB_STATE_COUNTY, layerId: 25 },
  city: { base: TIGERWEB_PLACES, layerId: 4 },
}

/** Degrees — keeps outlines light for client-side map highlight. */
const SIMPLIFY_OFFSET_BY_LEVEL: Record<TigerwebBoundaryLevel, number> = {
  state: 0.002,
  county: 0.0005,
  city: 0.0002,
}

const GEOID_RE = /^\d{2,7}$/

const memoryCache = new Map<string, TigerwebFeatureCollection>()
const pointResolveCache = new Map<string, ResolvedJurisdictionsAtPoint>()

function cacheKey(level: TigerwebBoundaryLevel, geoid: string): string {
  return `${level}:${geoid}`
}

function pointCacheKey(lat: number, lng: number): string {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`
}

export function isTigerwebBoundaryLevel(level: string): level is TigerwebBoundaryLevel {
  return level === 'state' || level === 'county' || level === 'city'
}

export function normalizeCensusGeoid(value: unknown): string | null {
  const raw = String(value ?? '').trim()
  if (!GEOID_RE.test(raw)) return null
  return raw
}

/** STUSAB exists only on the state layer; requesting it on county/city breaks TIGERweb queries. */
function outFieldsForLevel(level: TigerwebBoundaryLevel): string {
  return level === 'state'
    ? 'NAME,BASENAME,GEOID,STATE,STUSAB'
    : 'NAME,BASENAME,GEOID,STATE'
}

function buildQueryUrl(level: TigerwebBoundaryLevel, geoid: string): string {
  const { base, layerId } = LAYER_BY_LEVEL[level]
  const params = new URLSearchParams({
    where: `GEOID='${geoid}'`,
    outFields: outFieldsForLevel(level),
    returnGeometry: 'true',
    outSR: '4326',
    maxAllowableOffset: String(SIMPLIFY_OFFSET_BY_LEVEL[level]),
    f: 'geojson',
  })
  return `${base}/${layerId}/query?${params.toString()}`
}

function buildPointQueryUrl(level: TigerwebBoundaryLevel, lng: number, lat: number): string {
  const { base, layerId } = LAYER_BY_LEVEL[level]
  const params = new URLSearchParams({
    geometry: `${lng},${lat}`,
    geometryType: 'esriGeometryPoint',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: outFieldsForLevel(level),
    returnGeometry: 'false',
    f: 'json',
  })
  return `${base}/${layerId}/query?${params.toString()}`
}

type TigerwebJsonFeature = {
  attributes?: Record<string, unknown>
}

function parsePointFeature(
  level: TigerwebBoundaryLevel,
  feature: TigerwebJsonFeature | undefined,
): ResolvedJurisdiction | null {
  const attrs = feature?.attributes
  if (!attrs) return null
  const geoid = normalizeCensusGeoid(attrs.GEOID)
  if (!geoid) return null
  const name = String(attrs.NAME || attrs.BASENAME || geoid).trim()
  const basename = String(attrs.BASENAME || attrs.NAME || geoid).trim()
  const stusab = attrs.STUSAB != null ? String(attrs.STUSAB).trim().toUpperCase() : ''
  const stateFipsRaw = attrs.STATE != null ? String(attrs.STATE).trim() : ''
  const stateFips = /^\d{2}$/.test(stateFipsRaw)
    ? stateFipsRaw
    : geoid.length >= 2
      ? geoid.slice(0, 2)
      : null
  return {
    level,
    geoid,
    name,
    basename,
    stateAbbr: /^[A-Z]{2}$/.test(stusab) ? stusab : null,
    stateFips,
  }
}

function escapeArcGisWhereLiteral(value: string): string {
  return value.replace(/'/g, "''")
}

function buildNameSearchUrl(
  level: TigerwebBoundaryLevel,
  query: string,
  options?: { stateFips?: string | null; limit?: number },
): string {
  const { base, layerId } = LAYER_BY_LEVEL[level]
  const q = query.trim()
  const limit = Math.max(1, Math.min(options?.limit ?? 25, 50))
  const geoidExact = normalizeCensusGeoid(q)

  let where: string
  if (geoidExact) {
    where = `GEOID='${escapeArcGisWhereLiteral(geoidExact)}'`
  } else {
    const like = `%${escapeArcGisWhereLiteral(q.toUpperCase())}%`
    where = `(UPPER(NAME) LIKE '${like}' OR UPPER(BASENAME) LIKE '${like}')`
  }

  const stateFips = options?.stateFips?.trim()
  if (stateFips && /^\d{2}$/.test(stateFips) && level !== 'state') {
    where = `(${where}) AND STATE='${escapeArcGisWhereLiteral(stateFips)}'`
  }

  const params = new URLSearchParams({
    where,
    outFields: outFieldsForLevel(level),
    returnGeometry: 'false',
    orderByFields: 'NAME',
    resultRecordCount: String(limit),
    f: 'json',
  })
  return `${base}/${layerId}/query?${params.toString()}`
}

export type TigerwebSearchHit = {
  level: TigerwebBoundaryLevel
  geoid: string
  name: string
  basename: string
  stateAbbr: string | null
  stateFips: string | null
}

/**
 * Search Census TIGERweb jurisdictions by name/basename or exact GEOID.
 * Uses the same layers as boundary lookups (state / county / place).
 */
export async function searchTigerwebJurisdictions(
  level: TigerwebBoundaryLevel,
  query: string,
  options?: { signal?: AbortSignal; stateFips?: string | null; limit?: number },
): Promise<TigerwebSearchHit[]> {
  const q = query.trim()
  if (q.length < 2 && !normalizeCensusGeoid(q)) return []

  const res = await fetch(buildNameSearchUrl(level, q, options), {
    method: 'GET',
    signal: options?.signal,
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`TIGERweb search failed (${res.status})`)
  }
  const data = (await res.json()) as { features?: TigerwebJsonFeature[]; error?: unknown }
  if (data?.error) {
    throw new Error('TIGERweb search error')
  }
  const features = Array.isArray(data?.features) ? data.features : []
  const hits: TigerwebSearchHit[] = []
  for (const feature of features) {
    const parsed = parsePointFeature(level, feature)
    if (!parsed) continue
    hits.push(parsed)
  }
  return hits
}

/** Approximate centroid from a GeoJSON feature (bbox center). */
export function featureCentroid(
  feature: GeoJSON.Feature | null | undefined,
): { lat: number; lng: number } | null {
  if (!feature?.geometry) return null
  const coords: number[][] = []
  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) return
    if (node.length >= 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
      coords.push([node[0], node[1]])
      return
    }
    for (const child of node) walk(child)
  }
  walk((feature.geometry as GeoJSON.Geometry & { coordinates: unknown }).coordinates)
  if (coords.length === 0) return null
  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  for (const [lng, lat] of coords) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
  }
  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 }
}

export async function fetchTigerwebBoundary(
  level: TigerwebBoundaryLevel,
  geoidInput: string,
  options?: { signal?: AbortSignal },
): Promise<TigerwebFeatureCollection> {
  const geoid = normalizeCensusGeoid(geoidInput)
  if (!geoid) {
    throw new Error('Invalid Census GEOID')
  }

  const key = cacheKey(level, geoid)
  const cached = memoryCache.get(key)
  if (cached) return cached

  const res = await fetch(buildQueryUrl(level, geoid), {
    method: 'GET',
    signal: options?.signal,
    headers: { Accept: 'application/geo+json, application/json' },
  })

  if (!res.ok) {
    throw new Error(`TIGERweb request failed (${res.status})`)
  }

  const data = (await res.json()) as TigerwebFeatureCollection
  if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features) || data.features.length === 0) {
    throw new Error('No boundary found for this GEOID')
  }

  memoryCache.set(key, data)
  return data
}

/**
 * Fetch many boundaries for a level (unique GEOIDs), merging into one FeatureCollection.
 * Uses the in-memory cache; runs a small concurrency pool to avoid hammering TIGERweb.
 */
export async function fetchTigerwebBoundaries(
  level: TigerwebBoundaryLevel,
  geoidInputs: string[],
  options?: { signal?: AbortSignal; concurrency?: number },
): Promise<TigerwebFeatureCollection> {
  const unique = [
    ...new Set(
      geoidInputs
        .map((g) => normalizeCensusGeoid(g))
        .filter((g): g is string => Boolean(g)),
    ),
  ]
  if (unique.length === 0) {
    return { type: 'FeatureCollection', features: [] }
  }

  const concurrency = Math.max(1, Math.min(options?.concurrency ?? 4, unique.length))
  const features: GeoJSON.Feature[] = []
  let nextIndex = 0

  const workers = Array.from({ length: concurrency }, async () => {
    while (nextIndex < unique.length) {
      if (options?.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
      const idx = nextIndex
      nextIndex += 1
      const geoid = unique[idx]
      try {
        const fc = await fetchTigerwebBoundary(level, geoid, { signal: options?.signal })
        for (const feature of fc.features) {
          features.push({
            ...feature,
            properties: {
              ...(feature.properties || {}),
              GEOID: geoid,
              _overlayLevel: level,
            },
          })
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err
        // Skip missing / failed GEOIDs; keep other overlays.
      }
    }
  })

  await Promise.all(workers)
  return { type: 'FeatureCollection', features }
}

async function queryJurisdictionAtPoint(
  level: TigerwebBoundaryLevel,
  lat: number,
  lng: number,
  options?: { signal?: AbortSignal },
): Promise<ResolvedJurisdiction | null> {
  const res = await fetch(buildPointQueryUrl(level, lng, lat), {
    method: 'GET',
    signal: options?.signal,
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`TIGERweb ${level} point query failed (${res.status})`)
  }
  const data = (await res.json()) as { features?: TigerwebJsonFeature[]; error?: unknown }
  if (data?.error) {
    throw new Error(`TIGERweb ${level} point query error`)
  }
  const features = Array.isArray(data?.features) ? data.features : []
  return parsePointFeature(level, features[0])
}

/**
 * Resolve Census state / county / incorporated place containing a lat/lng.
 * City may be null for unincorporated areas.
 */
export async function resolveJurisdictionsAtPoint(
  lat: number,
  lng: number,
  options?: { signal?: AbortSignal },
): Promise<ResolvedJurisdictionsAtPoint> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error('Invalid coordinates')
  }

  const key = pointCacheKey(lat, lng)
  const cached = pointResolveCache.get(key)
  if (cached) return cached

  const settled = await Promise.allSettled([
    queryJurisdictionAtPoint('state', lat, lng, options),
    queryJurisdictionAtPoint('county', lat, lng, options),
    queryJurisdictionAtPoint('city', lat, lng, options),
  ])

  const pick = (i: number): ResolvedJurisdiction | null =>
    settled[i]?.status === 'fulfilled' ? settled[i].value : null

  const result: ResolvedJurisdictionsAtPoint = {
    state: pick(0),
    county: pick(1),
    city: pick(2),
  }
  // Only cache complete successes so transient failures can be retried.
  if (result.state || result.county || result.city) {
    pointResolveCache.set(key, result)
  }
  return result
}

export function clearTigerwebBoundaryCache(): void {
  memoryCache.clear()
  pointResolveCache.clear()
}
